import { validStr } from 'booksprout'
import { useLink } from '../modules/link/linkModule'
import { useSystem } from '../modules/system/systemModule'

/**
 * A module's own urls, and how to leave it.
 *
 * The five url builders are thin wrappers over `linkService`, which knows each app's route shapes; what
 * they add is the module name, so a page says `editUrl(id)` rather than repeating its own slug.
 *
 * `goBack` is the interesting one. Creating a book from inside "create an arc" sets a return location
 * in the system store, so going back has three outcomes in priority order: the stored location (with
 * the model to attach), an explicit fallback route, or one step back in history.
 *
 * `urlModelName` may arrive as a function OR a string. The mixin passes a getter, because it is built
 * once per component (cached in a computed) and a page could set its slug after construction; a
 * `setup()` component whose slug is a constant passes the string, which keeps a screen that only wants
 * `goBack` from carrying an arrow nothing ever calls.
 */

export interface LinksContext {
  urlModelName: string | (() => string)
  router?: {
    push (to: string): Promise<unknown> | void
    go (n: number): void
  }
}

export function useLinks (context: LinksContext) {
  const linkService = useLink().link()

  const modelName = () =>
    typeof context.urlModelName === 'function' ? context.urlModelName() : context.urlModelName

  const pushIgnoringRedirects = (to: string) => {
    const pushed = context.router?.push(to)
    if (pushed && typeof (pushed as Promise<unknown>).catch === 'function') {
      void (pushed as Promise<unknown>).catch(() => {
        // do nothing, routeGuard redirected the visitor — swallows the "Uncaught (in promise)
        // undefined" that a cancelled navigation throws
      })
    }
  }

  const links = {
    /**
     * Getters, not values: they read a `reactive()` store that changes while the page is open.
     */
    get returnLocation (): string {
      return useSystem().system.store.getters.returnLocation
    },

    get attachModelId (): number | undefined {
      return useSystem().system.store.getters.attachModelId
    },

    get stateReturnLabel (): string {
      return useSystem().system.store.getters.returnLabel
    },

    get stateCompleteLabel (): string {
      return useSystem().system.store.getters.completeLabel
    },

    createUrl: () => linkService.adminCreate(modelName()),
    editUrl: (id: any) => linkService.adminUpdate(modelName(), id),
    deleteUrl: (id: number) => linkService.adminDelete(modelName(), id),
    listUrl: () => linkService.adminList(modelName()),
    viewUrl: (id: any) => linkService.adminView(modelName(), id),

    /**
     * Back to the stored return location if there is one, else the fallback route, else one step back.
     */
    goBack (fallbackRoute?: string) {
      if (validStr(links.returnLocation)) {
        const url = new URL('https://booksprout.co' + links.returnLocation)
        const query = links.attachModelId ? `?attachModelId=${links.attachModelId}` : ''
        pushIgnoringRedirects(`${url.pathname}${query}${url.hash}`)
        useSystem().system.service.clearReturnLocation()
      } else if (fallbackRoute !== void 0 && validStr(fallbackRoute)) {
        pushIgnoringRedirects(fallbackRoute)
      } else {
        context.router?.go(-1)
      }
    },

    /**
     * "Back to X" reads the label the interrupted flow left behind, so a visitor sent here mid-arc is
     * told they are going back to the arc rather than to this module's list.
     */
    returnLabel: (defaultLabel: string) => {
      return validStr(links.stateReturnLabel) ? links.stateReturnLabel : defaultLabel
    },

    doneLabel: (defaultLabel: string) => {
      return validStr(links.stateCompleteLabel) ? links.stateCompleteLabel : defaultLabel
    }
  }

  return links
}
